import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { IconChevron } from '../icons/WizardIcons';
import { filterSelectOptions } from '../../utils/filterSearchableSelect';

type Props = {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  options: string[];
  disabled?: boolean;
  loading?: boolean;
  emptyMessage?: string;
  'aria-label'?: string;
};

/** Filter-bar combobox: click the field to type; options filter dynamically in the list below. */
export default function FilterSearchableSelect({
  id,
  value,
  onChange,
  placeholder,
  options,
  disabled = false,
  loading = false,
  emptyMessage = 'No data found',
  'aria-label': ariaLabel,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const showClear = Boolean(query.trim()) && !disabled;

  const filtered = useMemo(() => filterSelectOptions(options, query), [options, query]);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const openList = () => {
    if (disabled) return;
    setOpen(true);
    window.setTimeout(() => inputRef.current?.focus(), 0);
  };

  const listQuery = query.trim().toLowerCase();

  return (
    <div
      ref={wrapRef}
      className={`flt-search-sel-wrap${open ? ' is-open' : ''}${disabled ? ' is-disabled' : ''}${showClear ? ' has-clear' : ''}`}
    >
      <input
        ref={inputRef}
        id={id}
        type="text"
        className="flt-inp flt-search-sel-inp"
        value={query}
        disabled={disabled}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onChange={(e) => {
          const next = e.target.value;
          setQuery(next);
          onChange(next);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
      />
      {open && !disabled ? (
        <ul
          key={listQuery}
          id={listId}
          className="ms-chips-menu flt-search-sel-menu"
          role="listbox"
        >
          {loading ? (
            <li>
              <span className="ms-chips-empty">Loading…</span>
            </li>
          ) : filtered.length === 0 ? (
            <li>
              <span className="ms-chips-empty">{emptyMessage}</span>
            </li>
          ) : (
            filtered.map((label, index) => (
              <li key={`${label.toLowerCase()}-${index}`}>
                <button
                  type="button"
                  role="option"
                  aria-selected={label === value}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setQuery(label);
                    onChange(label);
                    setOpen(false);
                    inputRef.current?.blur();
                  }}
                >
                  {label}
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
      {showClear ? (
        <button
          type="button"
          className="flt-search-sel-clear"
          aria-label="Clear selection"
          tabIndex={-1}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            setQuery('');
            onChange('');
            setOpen(false);
            inputRef.current?.focus();
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M18 6L6 18" />
            <path d="M6 6l12 12" />
          </svg>
        </button>
      ) : null}
      <button
        type="button"
        className="flt-search-sel-chev-btn"
        tabIndex={-1}
        aria-label="Show options"
        disabled={disabled}
        onMouseDown={(e) => e.preventDefault()}
        onClick={openList}
      >
        <IconChevron />
      </button>
    </div>
  );
}
